import { useState, type PropsWithChildren } from "react";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Circle,
  Pencil,
  Search,
} from "lucide-react";
import { useApplication } from "../../../app/ApplicationState";
import type { AppRoute } from "../../../navigation/routes";
import {
  missionProgressLabel,
  missionTaskTitle,
  sortMissionTasks,
} from "../../../components/missionModel";
import { dimensionFor, type DimensionKey } from "./dimensions";
import { useDimensionRecords } from "./useDimensionRecords";
import type { DimensionRecord } from "./dimensionRecords";
import "./dimension-workspace.css";

const sections = [
  ["overview", "概览"],
  ["records", "相关记录"],
  ["tasks", "本周任务"],
  ["growth", "成长与管理"],
] as const;
type Section = (typeof sections)[number][0];
const details = {
  body: {
    prompt: "今天，给身体留一点时间。",
    capture: "记运动",
    source: "近期饮食、运动与健身笔记",
    growth: "查看健康指标、饮食分析和运动进展。",
    manage: "身体总览",
  },
  heart: {
    prompt: "此刻的感受，值得被记住。",
    capture: "记心情",
    source: "近期情绪记录",
    growth: "回顾情绪变化，找到适合自己的恢复节奏。",
    manage: "内心状态与恢复",
  },
  intellect: {
    prompt: "把今天的新发现，留成自己的知识。",
    capture: "记笔记",
    source: "近期笔记",
    growth: "查看知识积累，继续测验与自我探索。",
    manage: "知识与测验",
  },
  craft: {
    prompt: "每一次练习，都在积累。",
    capture: "记实践",
    source: "近期技术与实践笔记",
    growth: "查看学习途径、学习记录与技能进度。",
    manage: "学习途径",
  },
  bond: {
    prompt: "记住一次相遇，也记住一份关心。",
    capture: "记相处",
    source: "近期已关联人物的记录",
    growth: "查看人物档案、联系情况与关系变化。",
    manage: "人物与关系",
  },
  purpose: {
    prompt: "朝想去的方向，迈一小步。",
    capture: "复盘目标",
    source: "目标当前进度与最近复盘时间",
    growth: "拆解人生目标，跟进里程碑，留下阶段复盘。",
    manage: "目标与复盘",
  },
};
const shortcuts: Partial<
  Record<
    DimensionKey,
    { label: string; route: AppRoute; params?: Record<string, unknown> }[]
  >
> = {
  body: [
    { label: "健康指标", route: "health" },
    { label: "饮食管理", route: "nutrition" },
    { label: "运动训练", route: "fitness" },
  ],
  heart: [{ label: "精力管理", route: "energy" }],
  intellect: [
    { label: "知识测验", route: "exam" },
    { label: "性格探索", route: "mbti" },
  ],
  bond: [{ label: "泛灵之网", route: "pan-spirit" }],
};

export default function DimensionWorkspace({
  dimension,
  children,
}: PropsWithChildren<{ dimension: DimensionKey }>) {
  const state = useApplication();
  const {
    navigation,
    weeklyMission,
    missionLoading,
    missionError,
    dimensionScores,
    openDiaryCompose,
    loadGameStats,
  } = state;
  const d = dimensionFor(dimension);
  const copy = details[dimension];
  const params = navigation.current.params || {};
  const section: Section = sections.some(([key]) => key === params.section)
    ? (params.section as Section)
    : params.pathwayId || params.goalId
      ? "growth"
      : "overview";
  const { records, loading, error, reload } = useDimensionRecords(
    dimension,
    section !== "growth",
  );
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(30);
  const [taskStatus, setTaskStatus] = useState("all");
  const group = weeklyMission?.groups.find((g) => g.key === dimension);
  const tasks = sortMissionTasks(group?.tasks || []);
  const completed = tasks.filter((t) => t.status === "completed").length;
  const score = dimensionScores[dimension];
  const changeSection = (next: Section) => {
    if (section === "growth" && next !== "growth") loadGameStats();
    navigation.navigate(d.route, { section: next });
  };
  const capture = () => {
    if (dimension === "body") navigation.navigate("fitness", { record: true });
    else if (dimension === "purpose") changeSection("growth");
    else
      openDiaryCompose(
        (
          {
            heart: "mood",
            intellect: "note",
            craft: "tech",
            bond: "gratitude",
          } as const
        )[dimension],
      );
  };
  const openRecord = (r: DimensionRecord) => {
    if (r.goalId)
      navigation.navigate("goals", { section: "growth", goalId: r.goalId });
    else navigation.navigate("records", { recordKey: r.key });
  };
  const visibleRecords = records.filter((r) =>
    `${r.title} ${r.content} ${r.label}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );
  const visibleTasks = tasks.filter(
    (t) => taskStatus === "all" || t.status === taskStatus,
  );
  const recordsPanel = (compact: boolean) => (
    <section
      className="dimension-panel"
      aria-label={dimension === "purpose" ? "目标与复盘" : "相关记录"}
    >
      <div className="dimension-panel-heading">
        <div>
          <h2>
            {dimension === "purpose"
              ? "目标与复盘"
              : compact
                ? "最近记录"
                : "相关记录"}
          </h2>
          <p>{copy.source}</p>
        </div>
        {compact && (
          <button
            className="dimension-text-button"
            onClick={() => changeSection("records")}
          >
            查看记录 <ArrowRight size={15} />
          </button>
        )}
      </div>
      {!compact && (
        <label className="dimension-search">
          <Search size={17} />
          <input
            aria-label="搜索当前维度记录"
            placeholder="搜索当前列表的内容或名称"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(30);
            }}
          />
        </label>
      )}
      {!compact && dimension === "body" && (
        <p className="dimension-source-note">
          饮食展示最近 8 条，按日期回顾可前往
          <button
            className="dimension-text-button"
            onClick={() => navigation.navigate("nutrition")}
          >
            饮食管理 <ArrowUpRight size={14} />
          </button>
          。
        </p>
      )}
      {error && (
        <div className="dimension-notice" role="alert">
          {error}加载失败，已显示可用内容。
          <button onClick={reload}>重试</button>
        </div>
      )}
      {loading ? (
        <p className="dimension-empty" role="status">
          正在整理记录…
        </p>
      ) : (
        <>
          {(compact ? records.slice(0, 4) : visibleRecords.slice(0, limit)).map(
            (r) => (
              <button
                className="dimension-record"
                key={r.key}
                onClick={() => openRecord(r)}
              >
                <span className="dimension-record-meta">
                  <span>{r.label}</span>
                  <time>{r.day || "日期未记录"}</time>
                </span>
                <strong>{r.title}</strong>
                <span className="dimension-record-excerpt">{r.content}</span>
                <ArrowUpRight size={16} className="dimension-record-arrow" />
              </button>
            ),
          )}
          {!(compact ? records : visibleRecords).length && (
            <div className="dimension-empty">
              <p>
                {query && !compact
                  ? "没有匹配的记录，换个关键词试试。"
                  : error
                    ? "暂时无法显示记录。"
                    : "还没有相关内容，从今天的一小笔开始。"}
              </p>
              {!query && !error && (
                <button className="dimension-text-button" onClick={capture}>
                  {copy.capture} <ArrowRight size={15} />
                </button>
              )}
            </div>
          )}
          {!compact && visibleRecords.length > limit && (
            <button
              className="dimension-text-button dimension-more"
              onClick={() => setLimit((n) => n + 30)}
            >
              再看 30 条
            </button>
          )}
        </>
      )}
    </section>
  );
  const tasksPanel = (compact: boolean) => (
    <section className="dimension-panel" aria-label="当前维度任务">
      <div className="dimension-panel-heading">
        <div>
          <h2>{compact ? "本周行动" : "本周任务"}</h2>
          <p>
            {weeklyMission?.week_start
              ? `${weeklyMission.week_start} 起 · `
              : ""}
            把成长放进日常
          </p>
        </div>
        <button
          className="dimension-text-button"
          onClick={() =>
            compact
              ? changeSection("tasks")
              : navigation.navigate("missions", { dimension })
          }
        >
          {compact ? "查看全部" : "任务中心"} <ArrowRight size={15} />
        </button>
      </div>
      {!compact && (
        <div className="dimension-filters" aria-label="任务状态">
          {[
            ["all", "全部"],
            ["pending", "未完成"],
            ["completed", "已完成"],
          ].map(([key, name]) => (
            <button
              key={key}
              aria-pressed={taskStatus === key}
              onClick={() => setTaskStatus(key)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      {missionError && (
        <div className="dimension-notice" role="alert">
          任务更新失败。<button onClick={loadGameStats}>重试</button>
        </div>
      )}
      {missionLoading ? (
        <p className="dimension-empty" role="status">
          正在加载任务…
        </p>
      ) : (
        <>
          {(compact ? tasks.slice(0, 3) : visibleTasks).map((t) => (
            <button
              key={t.id}
              className="dimension-task"
              onClick={() =>
                navigation.navigate("missions", {
                  taskId: String(t.id),
                  dimension,
                })
              }
            >
              {t.status === "completed" ? (
                <CheckCircle2 size={20} />
              ) : (
                <Circle size={20} />
              )}
              <span>
                <strong>{missionTaskTitle(t)}</strong>
                <small>
                  {t.status === "completed"
                    ? "已完成"
                    : missionProgressLabel(t)}
                </small>
              </span>
              <ArrowRight size={16} />
            </button>
          ))}
          {!(compact ? tasks : visibleTasks).length && !missionError && (
            <div className="dimension-empty">
              <p>
                {taskStatus === "all" || compact
                  ? "本周还没有这个维度的任务。"
                  : "这个状态下暂无任务。"}
              </p>
              <button
                className="dimension-text-button"
                onClick={() => navigation.navigate("missions", { dimension })}
              >
                前往任务中心 <ArrowRight size={15} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
  return (
    <div className="dimension-workspace" data-dimension={dimension}>
      <header className="dimension-header">
        <div className="dimension-title">
          <span className="dimension-glyph" aria-hidden="true">
            {d.glyph}
          </span>
          <div>
            <p>六维生活 / {d.hint}</p>
            <h1>{d.name}</h1>
          </div>
        </div>
        <button
          className="desktop-primary"
          onClick={() => openDiaryCompose("categories")}
        >
          <Pencil size={17} />
          随手记一笔
        </button>
      </header>
      <div className="dimension-capture">
        <div>
          <strong>{copy.prompt}</strong>
          <p>
            {dimension === "purpose"
              ? "写下进展与思考，让下一步更清晰。"
              : "不用等到有完整的想法，先留下一点。"}
          </p>
        </div>
        <button onClick={capture}>
          {copy.capture}
          <ArrowUpRight size={18} />
        </button>
      </div>
      <nav className="dimension-sections" aria-label="维度内容">
        {sections.map(([key, label]) => (
          <button
            key={key}
            aria-current={section === key ? "page" : undefined}
            onClick={() => changeSection(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {section === "overview" && (
        <>
          <div className="dimension-stats">
            <button onClick={() => changeSection("growth")}>
              <span>当前成长分</span>
              <strong>
                {missionError || !Number.isFinite(score)
                  ? "—"
                  : Math.round(score)}
                <small>/ 100</small>
              </strong>
              <span>
                {missionError ? "暂时无法更新" : "查看维度详情"}{" "}
                <ArrowUpRight size={14} />
              </span>
            </button>
            <button onClick={() => changeSection("tasks")}>
              <span>本周任务完成</span>
              <strong>
                {missionError || missionLoading ? "—" : completed}
                <small>
                  / {missionError || missionLoading ? "—" : tasks.length}
                </small>
              </strong>
              <span>
                每一步都算数 <ArrowUpRight size={14} />
              </span>
            </button>
            <button onClick={() => changeSection("records")}>
              <span>
                {dimension === "purpose" ? "最近更新目标" : "最近留下的一笔"}
              </span>
              <strong className="dimension-latest">
                {loading
                  ? "加载中…"
                  : records[0]?.day || (error ? "暂不可用" : "从今天开始")}
              </strong>
              <span>
                {records[0]?.title || copy.capture} <ArrowUpRight size={14} />
              </span>
            </button>
          </div>
          <div className="dimension-columns">
            {recordsPanel(true)}
            <div className="dimension-side">
              {tasksPanel(true)}
              <section className="dimension-panel dimension-growth">
                <h2>成长，慢慢看得见</h2>
                <p>{copy.growth}</p>
                <button
                  className="dimension-text-button"
                  onClick={() => changeSection("growth")}
                >
                  {copy.manage} <ArrowRight size={16} />
                </button>
                <div className="dimension-shortcuts">
                  {shortcuts[dimension]?.map((s) => (
                    <button
                      key={s.route}
                      onClick={() => navigation.navigate(s.route, s.params)}
                    >
                      {s.label}
                      <ArrowUpRight size={14} />
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      )}
      {section === "records" && recordsPanel(false)}
      {section === "tasks" && tasksPanel(false)}
      {section === "growth" && (
        <section className="dimension-management" aria-label={copy.manage}>
          {children}
        </section>
      )}
    </div>
  );
}
